import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CHART_HELP } from '@dma/shared';
import { adminApi } from './admin-api.ts';
import { maturityRamp } from './format.ts';
import { DimensionHeatmap } from '../DimensionHeatmap.tsx';
import { Card, Select, Spinner } from './ui.tsx';
import { SpiderView } from './views/SpiderView.tsx';
import { QuadrantView } from './views/QuadrantView.tsx';
import { SummaryTableView } from './views/SummaryTableView.tsx';
import { DimensionDistributionView } from './views/DimensionDistributionView.tsx';
import { QuestionDistributionView } from './views/QuestionDistributionView.tsx';
import { CrossLobView } from './views/CrossLobView.tsx';

// The analysis body (LOB filter + six views), no shell/header — rendered inside a tab
// by EngagementDetail. Charts read the fixed Wipro accent (see index.css / maturityRamp).
export function AnalysisContent({ engagementId }: { engagementId: string }) {
  const [lobId, setLobId] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin-analysis', engagementId, lobId],
    queryFn: () => adminApi.analysis(engagementId, lobId),
  });

  const levelColors = useMemo(() => maturityRamp('#2e5bea'), []);

  if (query.isLoading) return <Spinner />;
  if (query.isError || !query.data) {
    return (
      <Card>
        <p className="py-8 text-center text-sm text-red-600" role="alert">
          Could not load analysis for this engagement.
        </p>
      </Card>
    );
  }

  const { lobs, views } = query.data;
  const lobOptions = [{ value: '', label: 'All LOBs' }, ...lobs.map((l) => ({ value: l.id, label: l.name }))];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-end">
        <div className="w-56">
          <Select
            id="lob-filter"
            label="Line of business"
            value={lobId ?? ''}
            onChange={(v) => setLobId(v === '' ? null : v)}
            options={lobOptions}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card
          title="Maturity radar"
          info={CHART_HELP.radar}
          description="Mean score per dimension (1–5). The summary table below is its accessible equivalent."
        >
          <SpiderView data={views.spider} />
        </Card>
        <Card
          title="Mean vs. spread quadrant"
          info={CHART_HELP.quadrant}
          description="Each dimension positioned by mean (maturity) and standard deviation (agreement)."
        >
          <QuadrantView data={views.quadrant} />
        </Card>
      </div>

      <Card
        title="Maturity heatmap"
        info={CHART_HELP.heatmap}
        description="Mean score per dimension at the current filter (1–5), shaded by maturity — deeper blue is higher."
      >
        <DimensionHeatmap items={views.spider.map((d) => ({ name: d.name, value: d.mean }))} />
      </Card>

      <Card
        title="Dimension summary"
        info={CHART_HELP.summary}
        description="Sorted worst-first by mean. Mean, median, standard deviation, quadrant and respondent count."
      >
        <SummaryTableView data={views.summaryTable} />
      </Card>

      <Card
        title="Per-dimension distribution"
        info={CHART_HELP.dimensionDistribution}
        description="Share of responses at each maturity level (1–5) for the selected dimension."
      >
        <DimensionDistributionView data={views.perDimensionDistribution} colors={levelColors} />
      </Card>

      <Card
        title="Per-question distribution"
        info={CHART_HELP.questionDistribution}
        description="Every question in a dimension with its distribution and divergence flag."
      >
        <QuestionDistributionView data={views.perQuestionDistribution} colors={levelColors} />
      </Card>

      <Card
        title="Cross-LOB comparison"
        info={CHART_HELP.crossLob}
        description="Dimension means across lines of business, shaded by maturity (deeper is higher). Range = max − min per dimension."
      >
        <CrossLobView data={views.crossLob} lobs={lobs} />
      </Card>
    </div>
  );
}
